import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ConfirmDialog,
  type ConfirmOptions,
} from "@/components/ui/ConfirmDialog";

// Промис-подтверждение вместо window.confirm:
//   const confirm = useConfirm();
//   if (!(await confirm({ title: "Удалить сайт?", text: "Реги останутся.", danger: true }))) return;
// Требует <ConfirmProvider> выше по дереву (подключается в App).
// Без провайдера — честный запасной путь через window.confirm.

type Ask = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<Ask | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((opts) => {
    // Предыдущий незакрытый вопрос считаем отменённым.
    resolver.current?.(false);
    setState(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const finish = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setState(null);
  };

  return (
    <Ctx.Provider value={ask}>
      {children}
      <ConfirmDialog
        open={state != null}
        {...(state ?? {})}
        onConfirm={() => finish(true)}
        onCancel={() => finish(false)}
      />
    </Ctx.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(Ctx);
  return (
    ask ??
    (async (opts) =>
      // Запасной путь без ConfirmProvider — нативный диалог здесь намеренно.
      // eslint-disable-next-line no-restricted-syntax
      window.confirm(
        [opts.title, opts.text].filter((x) => typeof x === "string").join("\n"),
      ))
  );
}

export default useConfirm;
